import { ConsentManagementConfigVersion } from "@/api";
import {
  ConsentManagementSlug,
  fpConsentManagementCategoryMap,
} from "@/consent-management/consentManagement";
import {
  consentManagerConfigSchema,
  initialValues,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import {
  countConsentConfigDiffs,
  diffConsentConfigs,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/utils";
import { DestinationConfigSaasDestinationPair } from "@/hooks/useDestinationConfigSaasDestinationPairs";
import { LoadedConsentManagementInfo } from "@/types/global";

export const AUDIENCES_MAPPING_KEY = "Audiences";

export function findOtherLiveConfig(
  configs: Partial<Record<ConsentManagementSlug, LoadedConsentManagementInfo>>,
): LoadedConsentManagementInfo | undefined {
  return Object.values(configs).find(
    (other): other is LoadedConsentManagementInfo =>
      !!other &&
      other.type !== "freshpaint" &&
      other.enabled &&
      !!other.activePublishedVersion,
  );
}

export function countUnpublishedChanges(
  draft: ConsentManagementConfigVersion,
  lastPublished: ConsentManagementConfigVersion | undefined,
): number {
  return countConsentConfigDiffs(
    diffConsentConfigs({ config1: lastPublished, config2: draft }),
  );
}

function mappingDisplayName(mappingKey: string): string {
  return mappingKey === AUDIENCES_MAPPING_KEY
    ? "audience destinations"
    : mappingKey.replace("_dot_", ".");
}

/**
 * Runs the same validation the Consent Manager page runs before it lets you
 * publish, and returns one message per problem.
 */
export function findPublishBlockers({
  draft,
  destinations,
  audiencesEnabled,
  sessionReplayEntitled,
}: {
  draft: ConsentManagementConfigVersion;
  destinations: DestinationConfigSaasDestinationPair[];
  audiencesEnabled: boolean;
  sessionReplayEntitled: boolean;
}): string[] {
  const result = consentManagerConfigSchema.safeParse(
    initialValues(
      draft,
      destinations,
      audiencesEnabled,
      sessionReplayEntitled,
      true,
    ),
  );
  if (result.success) {
    return [];
  }
  return [
    ...new Set(
      result.error.issues.map((issue) =>
        issue.path[0] === "consentMapping"
          ? `Choose a consent category for ${mappingDisplayName(String(issue.path[1]))}`
          : issue.message,
      ),
    ),
  ];
}

export function findUncategorized(
  destinations: DestinationConfigSaasDestinationPair[],
  consentMapping: Record<string, string> | undefined,
): DestinationConfigSaasDestinationPair[] {
  return destinations.filter(
    ({ destinationConfig }) => !consentMapping?.[destinationConfig.name],
  );
}

/** Audience destinations are ad platforms, so they default to marketing. */
export function suggestedAudiencesCategory(
  categoryMapping: Record<string, string>,
): string {
  const marketing = fpConsentManagementCategoryMap.freshpaint.marketing;
  return marketing in categoryMapping ? marketing : "";
}
